import { Capacitor } from "@capacitor/core";
import {
  IonBackButton,
  IonButton,
  IonButtons,
  IonContent,
  IonHeader,
  IonItem,
  IonPage,
  IonSpinner,
  IonToolbar,
} from "@ionic/react";
import React from "react";
import DataController from "@controllers/DataController";
import { useHistory, useParams } from "react-router-dom";
import HeaderTitle from "@components/common/HeaderTitle";
import { useProgramData } from "@hooks/data/useProgramData";
import * as StringUtils from "@utils/stringUtils";
import ProgramInfoCard from "@components/home/ProgramInfoCard";
import ProgramEvidenceCard from "@components/programs/ProgramEvidenceCard";
import { IEvidence, IUser, ProgramSkill } from "@interfaces/Interfaces";
import { useRecoilValueLoadable } from "recoil";
import { evidenceAtom, userAtom } from "@state/State";
import _ from "lodash";
import { Button_Disclosure_Right_Grey, Icon_Refresh } from "@assets/images";
import * as ProgramUtils from "@utils/programUtils";
import SkillsRAGDisplay from "@components/programs/SkillsRAGDisplay";
import { useRefreshProgramData } from "@hooks/programs/useRefreshProgramData";

const ProgramSkillsLogPage: React.FC = () => {
  const history = useHistory();
  const params = useParams<{ programId: string; skillSlug: string }>();

  const { userPrograms } = useProgramData();
  const user = useRecoilValueLoadable<IUser | null>(userAtom);
  const { isRefreshingProgramData, refreshUserProgramData } = useRefreshProgramData();

  const program = userPrograms.find((item) => item.ID === params.programId);
  const currentLevel = program ? ProgramUtils.getSkillLevelForProgram(user.contents, program) : "";
  const skillsGroup = ProgramUtils.filterSkillsByLevel(
    program?.Skills?.filter((item) => StringUtils.toSlug(item.Section ?? "") === params.skillSlug) ?? [],
    currentLevel
  );
  const groupName = skillsGroup?.[0]?.Section;

  const allEvidence = useRecoilValueLoadable<IEvidence[]>(evidenceAtom);
  const sectionSkillIds = new Set(skillsGroup.map((skill) => skill.ID));

  let evidenceWithSkills: IEvidence[] = [];
  let nonDraftEvidenceWithSkills: IEvidence[] = [];
  if (allEvidence.state === "hasValue" && allEvidence.contents && program) {
    evidenceWithSkills = allEvidence.contents.filter((evidence: IEvidence) => {
      const programInfo = evidence.evidenceJSON?.programInfo;
      if (programInfo?.programID !== program.ID) {
        return false;
      }
      return sectionSkillIds.has(programInfo?.skillID);
    });

    evidenceWithSkills = _.orderBy(evidenceWithSkills, "date", "desc");
    nonDraftEvidenceWithSkills = evidenceWithSkills.filter((e) => e.draft !== true);
  }

  const evidenceCardClicked = (item: IEvidence): void => {
    history.push(`/dashboard/program/${params.programId}/read/${item.id}`);
  };

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar
          mode="ios"
          className="navBar"
          style={{
            maxWidth: DataController.isWeb() ? 980 : undefined,
            height: Capacitor.getPlatform() === "android" ? "54px" : "unset",
          }}
        >
          <IonButtons slot="start">
            <IonBackButton
              className="header-back-buttons"
              defaultHref={`/dashboard/program/${params.programId}`}
              text={DataController.getBackIconText()}
              icon={DataController.getBackIconType()}
              style={{
                marginLeft: Capacitor.getPlatform() === "android" ? 8 : 0,
                "--icon-font-size": Capacitor.getPlatform() === "android" ? "24px" : "30px",
              }}
            />
          </IonButtons>
          <HeaderTitle>{groupName ?? "Skills Log"}</HeaderTitle>
          <IonButtons slot="end">
            <IonButton
              disabled={isRefreshingProgramData}
              className="header-button"
              mode="ios"
              onClick={refreshUserProgramData}
            >
              {isRefreshingProgramData ? (
                <IonSpinner className="h-[20px] w-[44px] text-white" />
              ) : (
                <img
                  src={Icon_Refresh}
                  className="headerIcon"
                />
              )}
            </IonButton>
          </IonButtons>
        </IonToolbar>
      </IonHeader>
      <IonContent className="page-background">
        <ProgramInfoCard
          title={groupName ?? "Skills Log"}
          fullWidthChildren
          style={{ paddingBottom: 0 }}
        >
          <div className="mx-4 mb-3">
            <SkillsRAGDisplay
              skills={skillsGroup ?? []}
              evidence={nonDraftEvidenceWithSkills}
              showHeaderOnly
            />
          </div>
          <SkillsLogList
            skills={skillsGroup ?? []}
            evidence={nonDraftEvidenceWithSkills}
          />
        </ProgramInfoCard>
        <ProgramInfoCard
          title="Evidence list"
          style={{ paddingTop: 14, paddingBottom: 8 }}
          subTitle={evidenceWithSkills.length === 0 ? "Any evidence added will appear below." : undefined}
        >
          {evidenceWithSkills.length > 0 && (
            <div className="mt-3">
              {evidenceWithSkills.map((item) => (
                <ProgramEvidenceCard
                  key={item.id}
                  buttonPressed={() => evidenceCardClicked(item)}
                  programEvidence={item}
                  program={program}
                  progressCheckLocked={false}
                />
              ))}
            </div>
          )}
        </ProgramInfoCard>
      </IonContent>
    </IonPage>
  );
};

type SkillsLogListProps = {
  skills: ProgramSkill[];
  evidence: IEvidence[];
};

const SkillsLogList: React.FC<SkillsLogListProps> = (props) => {
  const groupedSkills = _.groupBy(props.skills, "Top section");

  return (
    <div>
      {Object.keys(groupedSkills)
        .sort()
        .map((key, index) => {
          const groupSkillIds = new Set(groupedSkills[key].map((s) => s.ID));
          const groupEvidence = props.evidence.filter((e) => groupSkillIds.has(e.evidenceJSON?.programInfo?.skillID));
          return (
            <SkillsLogGroup
              key={key}
              index={index + 1}
              skills={groupedSkills[key]}
              evidence={groupEvidence}
            />
          );
        })}
    </div>
  );
};

type SkillsLogGroupProps = {
  index: number;
  skills: ProgramSkill[];
  evidence: IEvidence[];
};

const SkillsLogGroup: React.FC<SkillsLogGroupProps> = (props) => {
  const history = useHistory();
  const params = useParams<{ programId: string; skillSlug: string }>();

  const section = props.skills[0]["Top section"] ?? "";
  const skillsCount = props.skills.length;
  const nonRejectedEvidence = props.evidence.filter((e) => e.evidenceJSON?.programInfo?.approvalState !== "Rejected");
  const skillsWithSummative = new Set(
    nonRejectedEvidence
      .filter((e) => e.evidenceJSON?.programInfo?.assessmentType === "Summative")
      .map((e) => e.evidenceJSON?.programInfo?.skillID)
      .filter(Boolean)
  );
  const summativeCount = props.skills.filter((skill) => skillsWithSummative.has(skill.ID)).length;
  const formativeCount = props.skills.filter((skill) => {
    const hasFormative = nonRejectedEvidence.some(
      (e) =>
        e.evidenceJSON?.programInfo?.skillID === skill.ID && e.evidenceJSON?.programInfo?.assessmentType === "Formative"
    );
    return hasFormative && !skillsWithSummative.has(skill.ID);
  }).length;

  const approvedEvidencePerSkill = new Map(
    props.skills.map((skill) => [
      skill.ID,
      props.evidence.filter(
        (e) =>
          e.evidenceJSON?.programInfo?.skillID === skill.ID && e.evidenceJSON?.programInfo?.approvalState !== "Pending"
      ).length,
    ])
  );
  const notEvidencedCount = props.skills.filter((skill) => (approvedEvidencePerSkill.get(skill.ID) ?? 0) === 0).length;
  const evidenceCount = props.evidence.length;

  const navigateToSection = (): void => {
    history.push(
      `/dashboard/program/${params.programId}/program-skills-log/${params.skillSlug}/${StringUtils.toSlug(section)}`
    );
  };

  return (
    <IonItem
      button
      detail={false}
      className="ion-item-no-padding"
      onClick={navigateToSection}
    >
      <div className="min-h-13 flex w-full flex-row items-center justify-between gap-2 py-2 !pl-safe-area-l !pr-safe-area-r">
        <div className="flex grow flex-row justify-between gap-4">
          <div className="flex flex-col gap-2">
            <div className="text-acc-16px font-acc-500 text-grey-90">{section}</div>
            <div className="flex-shrink-1 flex flex-row flex-wrap gap-1">
              <div className="flex flex-row">
                <div className="flex flex-shrink-0 rounded-full bg-green-light-text px-2 py-1">
                  <span className="text-acc-11px font-semibold leading-none text-white">{`${summativeCount} Summative`}</span>
                </div>
              </div>
              <div className="flex flex-row">
                <div className="flex flex-shrink-0 rounded-full bg-orange-400 px-2 py-1">
                  <span className="text-acc-11px font-semibold leading-none text-white">{`${formativeCount} Formative`}</span>
                </div>
              </div>
              <div className="flex flex-row">
                <div className="flex flex-shrink-0 rounded-full bg-grey-60 px-2 py-1">
                  <span className="text-acc-11px font-semibold leading-none text-white">{`${notEvidencedCount} Not Evidenced`}</span>
                </div>
              </div>
            </div>
          </div>
          <div className="mt-1 flex flex-shrink-0 flex-row justify-start gap-0.5 self-center">
            <div className="text-acc-11px font-medium leading-[1.27] text-grey-60">
              {`${evidenceCount} EVIDENCE ${StringUtils.DOT_SEPARATOR} ${skillsCount} COMPETENCIES`}
            </div>
          </div>
        </div>
        <img
          src={Button_Disclosure_Right_Grey}
          alt=""
          className="img-no-select size-6"
        />
      </div>
    </IonItem>
  );
};

export default ProgramSkillsLogPage;
